import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { getLogsList, handlePaginate } from "store/logs/slice";
import { gamesListLimit, gamesListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import LogsFilter from "./components/LogsFilter";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";

import styles from "./List.module.scss";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const LogsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();

  const [services, setServices] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [currentLimit, setCurrentLimit] = useState(gamesListLimit);

  const logsList = useAppSelector((store) => store.logs.list);
  const isListFetched = useAppSelector((store) => store.logs.isListFetched);
  const totalItems = useAppSelector((store) => store.logs.totalItems);
  const totalPages = useAppSelector((store) => store.logs.totalPages);
  const activePage = useAppSelector((store) => store.logs.activePage);

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'logs',
    isListFetched
  });

  const getFilterString = () => {
    if (location.pathname.includes("errors")) {
      return { types: ["critical"] };
    }
    if (location.pathname.includes("warnings")) {
      return { types: ["warning"] };
    }
    return { types: ["notice"] };
  };

  useEffect(() => {
    dispatch(handlePaginate(1));
  }, [location]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getLogsList({
          limit: currentLimit,
          offset: activePage - 1,
          services: services.map((el) => el.value as string),
          ...getFilterString(),
        }),
      );
    }
  }, [isListFetched, activePage, location, services, currentLimit]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <LogsFilter
        selectedServices={services}
        handleUpdateServices={(e) => setServices(e)}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box"></div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={5} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap table">
            {logsList.length === 0 && <EmptyState message={t("noLogsFound")} />}

            {logsList.length > 0 && (
              <Table
                data={logsList}
                idField="hash"
                hasCheckbox={false}
                hasOptions={false}
                className={styles.logsTable}
                customColumns={(row) => ({
                  message: (
                    <div className={styles.messageCell}>
                      <span className={styles.messagePreview}>{row.message}</span>
                      <CopyToClipboard
                        text={row.message}
                        notificationMessage=""
                        className={styles.copyButton}
                      />
                    </div>
                  ),
                })}
              />
            )}
          </div>
        )}

        {isListFetched && logsList.length > 0 && (
          <Pagination
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={gamesListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              dispatch(handlePaginate(1));
            }}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default LogsList;
